import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import {
  Box,
  Button,
  Typography,
  TablePagination,
  TextField,
  InputAdornment,
  Tooltip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  IconButton,
  Alert,
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LockIcon from '@mui/icons-material/Lock';
import WarningIcon from '@mui/icons-material/Warning';
import { getAssociateCredentials } from '../../services/associate';
import { viewUrl } from '../../services/s3';
import AlertModal from '../../components/AlertModal';
import OTPVerificationModal from '../../components/OTPVerificationModal/OTPVerificationModal';
import { exportAsExcel, exportAsCSV, exportAsPDF } from '../../utils/importExport';
import ExportButton from '../../components/ExportButton/ExportButton';
import { hasReadPermission } from '../../utils/menuFilter';

function normalizeAssociate(item) {
  const sponsor =
    Array.isArray(item.sponsorDetail) && item.sponsorDetail[0]
      ? item.sponsorDetail[0]
      : item.sponsor || item.sponsorInfo || item.sponsorDetails || item.sponsorData || {};

  return {
    _id: item._id || '',
    associatedId: String(item.associateId ?? item._id ?? ''),
    name: item.name ?? '',
    mobile: item.phone ?? item.whatsapp ?? '',
    loginId: item.userAccess?.loginId ?? '',
    password: item.userAccess?.password ?? '',
    sponsorName: sponsor.sponsorName ?? sponsor.name ?? item.sponsorName ?? item.sponsor?.name ?? '',
    sponsorMobile:
      sponsor.sponsorMobile ??
      sponsor.mobile ??
      sponsor.phone ??
      item.sponsorMobile ??
      item.sponsor?.mobile ??
      item.sponsor?.phone ??
      '',
    sponsorLoginId:
      sponsor.sponsorLoginId ??
      item.sponsorInfo?.sponsorLoginId ??
      '',
    sponsorId:
      sponsor.sponsorId ??
      sponsor.associateId ??
      item.sponsorId ??
      item.sponsorBy ??
      '',
    image: item.img || item.imgUrl || '',
    designation: item.designation ?? '',
    status: item.isActive ? 'Active' : 'Inactive',
  };
}

const S3Avatar = ({ src, ...props }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (src) {
      viewUrl(src).then(setUrl).catch(console.error);
    }
  }, [src]);
  return <Avatar src={url} {...props} />;
};

const AssociateCredentials = () => {
  const history = useHistory();

  const canRead = hasReadPermission('associate.associate-credentials') ||
    hasReadPermission('associate-credentials') ||
    hasReadPermission('associate.associate-list');

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [allAssociates, setAllAssociates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [totalDocs, setTotalDocs] = useState(0);

  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(true);

  const [alertModal, setAlertModal] = useState({ open: false, title: 'Alert', message: '' });
  const showAlert = (message, title = 'Alert') => setAlertModal({ open: true, title, message });

  const fetchAssociates = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
      };
      if (search) params.filterKey = search;

      const json = await getAssociateCredentials(params);
      if (json.status && json.data) {
        const list = json.data.map(normalizeAssociate);
        setData(list);
        setTotalDocs(
          json.pagination?.totalItems || 
          json.extra?.total || 
          json.totalCount || 
          list.length
        );
      }
    } catch (e) {
      console.error('Fetch Error:', e);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search]);

  useEffect(() => {
    if (isOtpVerified) {
      fetchAssociates();
    }
  }, [fetchAssociates, isOtpVerified]);

  const handleReset = () => {
    setSearch('');
    setPage(0);
  };

  const buildExportRows = (list) => {
    return list.map((item) => ({
      'Associate ID': item.associatedId,
      'Name': item.name,
      'Designation': item.designation,
      'Mobile': item.mobile,
      'Login ID': item.loginId,
      'Password': item.password,
      'Sponsor Name': item.sponsorName,
      'Sponsor Login ID': item.sponsorLoginId || item.sponsorId,
      'Sponsor Mobile': item.sponsorMobile,
      'Status': item.status,
    }));
  };

  const handleExportPdf = () => {
    const rows = buildExportRows(data);
    exportAsPDF({ rows, title: 'Associate Credentials', fileNameBase: 'Associate_Credentials' });
  };

  const handleExportCsv = () => {
    const rows = buildExportRows(data);
    exportAsCSV({ rows, fileNameBase: 'Associate_Credentials' });
  };

  const handleExportExcel = () => {
    const rows = buildExportRows(data);
    exportAsExcel({ rows, sheetName: 'Credentials', fileNameBase: 'Associate_Credentials' });
  };


  if (!canRead) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh', p: 3 }}>
        <Paper elevation={3} sx={{ p: { xs: 3, md: 6 }, textAlign: 'center', maxWidth: 500, borderRadius: 3, boxShadow: '0 10px 40px rgba(0,0,0,0.1)' }}>
          <Avatar sx={{ bgcolor: '#fee2e2', color: '#ef4444', width: 80, height: 80, mb: 3, mx: 'auto' }}>
            <WarningIcon sx={{ fontSize: 50 }} />
          </Avatar>
          <Typography variant="h4" fontWeight="bold" color="#1a202c" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, fontSize: '1.1rem' }}>
            You do not have permission to view Associate Credentials. Please contact your administrator for access.
          </Typography>
          <Button
            variant="contained"
            onClick={() => history.push('/app/dashboard')}
            sx={{
              backgroundColor: '#536DFE',
              '&:hover': { backgroundColor: '#3d5afe' },
              px: 4, py: 1.2, borderRadius: 2, fontWeight: 600, textTransform: 'none'
            }}
          >
            Back to Dashboard
          </Button>
        </Paper>
      </Box>
    );
  }

  if (!isOtpVerified) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 }, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
        <OTPVerificationModal 
          open={showOtpModal} 
          onClose={() => history.push('/app/dashboard')}
          onSuccess={() => {
            setIsOtpVerified(true);
            setShowOtpModal(false);
          }}
          reason="Associate Credentials"
        />
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>Associate Credentials</Typography>
          <Typography variant="body2" color="text.secondary">Security verification required</Typography>
          <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
            OTP has been sent to your registered mobile number. Please enter it in the modal to proceed.
          </Alert>
        </Box>
        <Paper sx={{ p: 4, borderRadius: 2, height: '50vh', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.6 }}>
          <Box textAlign="center">
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="h6">Waiting for security verification...</Typography>
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>Associate Credentials</Typography>
          <Typography variant="body2" color="text.secondary">View and manage associate login details</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <ExportButton onExportPdf={handleExportPdf} onExportCsv={handleExportCsv} onExportExcel={handleExportExcel} />
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchAssociates} sx={{ borderRadius: 2 }}>Refresh</Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search by Name, Mobile, or Login ID..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
      </Paper>

      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead sx={{ bgcolor: '#536DFE' }}>
            <TableRow>
              <TableCell sx={{ color: 'white', fontWeight: 700 }}>Associate</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700 }}>Designation</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700 }}>Login ID</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700 }}>Password</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700 }}>Sponsor Info</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={4} align="center" sx={{ py: 10 }}><CircularProgress /></TableCell></TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 10 }}>
                  <Typography variant="h6">No records found</Typography>
                  <Button variant="text" onClick={handleReset}>Clear search</Button>
                </TableCell>
              </TableRow>
            ) : data.map((associate) => (
              <TableRow key={associate.associatedId} hover>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <S3Avatar src={associate.image} sx={{ width: 40, height: 40, border: '1px solid #eee' }} />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{associate.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{associate.mobile}</Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>{associate.designation || '—'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{associate.loginId || '—'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: '#f0f2f5', px: 1, py: 0.5, borderRadius: 1, display: 'inline-block' }}>
                    {associate.password || '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{associate.sponsorName || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">ID: {associate.sponsorLoginId || associate.sponsorId || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">Ph: {associate.sponsorMobile || '—'}</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalDocs}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />
      </TableContainer>

      <AlertModal open={alertModal.open} title={alertModal.title} message={alertModal.message} onClose={() => setAlertModal({ ...alertModal, open: false })} />
    </Box>
  );
};

export default AssociateCredentials;
